import React from 'react';
import { Box, Divider, Typography, CircularProgress } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import MessageBubble from './MessageBubble';
import LoadMoreIndicator from './LoadMoreIndicator';
import EmptyChatState from './EmptyChatState';

const MessagesList = ({
  messagesArray,
  loadingMessages,
  loadingMoreMessages,
  hasMoreMessages,
  selectedUser,
  userDetails,
  openChatAtRef,
  messagesContainerRef,
  topSentinelRef,
  messagesEndRef,
  downloadProgress,
  onDownload,
  onReact,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Box
      ref={messagesContainerRef}
      sx={{
        flex: 1,
        overflowY: 'auto',
        px: { xs: 1.5, sm: 2.5 },
        py: 2,
        display: 'flex',
        flexDirection: 'column',
        '&::-webkit-scrollbar': { width: '4px' },
        '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
        '&::-webkit-scrollbar-thumb': {
          bgcolor: isDark
            ? alpha(theme.palette.common.white, 0.1)
            : alpha(theme.palette.common.black, 0.1),
          borderRadius: 10,
        },
      }}
    >
      {loadingMessages ? (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress size={28} />
        </Box>
      ) : (
        <>
          <Box ref={topSentinelRef} sx={{ height: 1 }} />
          <LoadMoreIndicator
            isLoading={loadingMoreMessages}
            hasMore={hasMoreMessages}
          />

          {messagesArray.length === 0 ? (
            <EmptyChatState selectedUser={selectedUser} />
          ) : (
            <>
              {(() => {
                const openAt = openChatAtRef?.current;
                let lastSeenIndex = -1;
                for (let i = messagesArray.length - 1; i >= 0; i -= 1) {
                  const m = messagesArray[i];
                  if (
                    m?.senderId?._id === userDetails?.id &&
                    m?.isSeen
                  ) {
                    lastSeenIndex = i;
                    break;
                  }
                }
                return messagesArray.map((msg, index) => {
                  const isSelf =
                    msg?.senderId?._id === userDetails?.id;
                  const msgTime = msg?.timestamp
                    ? new Date(msg.timestamp).getTime()
                    : 0;
                  const showNewDivider =
                    openAt != null &&
                    msgTime > openAt &&
                    (index === 0 ||
                      (messagesArray[index - 1]?.timestamp &&
                        new Date(
                          messagesArray[index - 1].timestamp
                        ).getTime() <= openAt));
                  const showEyeIcon =
                    isSelf && index === lastSeenIndex;
                  return (
                    <React.Fragment key={msg._id || index}>
                      {showNewDivider && (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            py: 1.5,
                          }}
                        >
                          <Divider sx={{ flex: 1 }} />
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'primary.main',
                              fontWeight: 600,
                              fontSize: '0.7rem',
                            }}
                          >
                            New messages
                          </Typography>
                          <Divider sx={{ flex: 1 }} />
                        </Box>
                      )}
                      <MessageBubble
                        message={msg}
                        isSelf={isSelf}
                        senderImage={msg?.senderId?.image}
                        senderName={`${msg?.senderId?.fname || ''} ${msg?.senderId?.lname || ''}`.trim()}
                        showEyeIcon={showEyeIcon}
                        downloadProgress={downloadProgress}
                        onDownload={onDownload}
                        onReact={onReact}
                        currentUserId={userDetails?.id}
                      />
                    </React.Fragment>
                  );
                });
              })()}
            </>
          )}
          <div ref={messagesEndRef} />
        </>
      )}
    </Box>
  );
};

export default MessagesList;
